import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { DTO__position } from '@shared/dto/+image/image_asset_base_generation/DTO__position';
import { invert_hex } from '@shared/utils/utils_colors';
import { useEffect, useRef, useState } from 'react';
import { ENUM__image_brush, ENUM_UTILS__image_brush } from '../../_base/ENUM__image_brush';

export function G_CURSOR__brush_draw() {
  /*
   * STORE
   */

  const { g_image_edit__brush_size, g_image_edit__brush, g_general__ui_disabled } = use_slice_ui([
    'g_image_edit__brush_size',
    'g_image_edit__brush',
    'g_general__ui_disabled',
  ]);

  /*
   * STATE
   */

  const [cursor_position, set_cursor_position] = useState(DTO__position.CREATE_STUB());

  const ref_container = useRef(ENUM__html_ref_stub.DIV);

  const [width, height] = [g_image_edit__brush_size, g_image_edit__brush_size];

  /*
   * EFFECTS
   */

  useEffect(() => {
    window.addEventListener('mousemove', on_mouse_move);
    return () => window.removeEventListener('mousemove', on_mouse_move);
  }, []);

  /*
   * EVENTS
   */

  function on_mouse_move(event: MouseEvent) {
    const rect = ref_container.current.getBoundingClientRect();

    // calculate position relative to container
    const [x, y] = [event.clientX - rect.left, event.clientY - rect.top];

    set_cursor_position(new DTO__position(x, y));
  }

  /*
   * METHODS
   */

  function is_mouse_over() {
    return (
      cursor_position.x > 0 &&
      cursor_position.y > 0 &&
      cursor_position.x < ref_container.current.clientWidth &&
      cursor_position.y < ref_container.current.clientHeight
    );
  }

  /*
   * RENDERS
   */

  function render_cursor() {
    if (!is_mouse_over()) return null;
    if (g_image_edit__brush == ENUM__image_brush.POINTER) return null;
    if (g_general__ui_disabled) return null;

    let backgroundColor = ENUM_UTILS__image_brush.get_brush_color(g_image_edit__brush);
    const borderColor = invert_hex(backgroundColor);

    // Calculate the radius of the brush to offset the icon perfectly outside of it
    const radius = width / 2;
    // Add a small padding (e.g., 8px) so the icon isn't choking right against the border
    const iconPadding = 8;
    const offset = radius + iconPadding;

    return (
      <>
        {/* Brush Circle: Centered exactly on the mouse point */}
        <div
          className="absolute rounded-full border-[2px] opacity-50 flex-shrink-0 pointer-events-none"
          style={{
            width,
            height,
            backgroundColor,
            borderColor,
            transform: 'translate(-50%, -50%)',
          }}
        />

        {/* Icon: Positioned relative to the 0x0 mouse point, shifted by the brush radius */}
        <div
          className="absolute flex-center pointer-events-none"
          style={{
            // Keeps it at the top-right edge of the circle boundary
            transform: `translate(${offset}px, -${offset}px)`,
          }}
        >
          {ENUM_UTILS__image_brush.get_cursor_icon(g_image_edit__brush, '!size-5')}
        </div>
      </>
    );
  }

  /*
   * HTML
   */

  return (
    <div ref={ref_container} className="info_cursor_brush_draw absolute top-0 size-full pointer-events-none cursor-none">
      {/* 0x0 structural anchor tracking the mouse coordinates exactly */}
      <div className="absolute pointer-events-none z-50 size-0" style={{ left: cursor_position.x, top: cursor_position.y }}>
        {render_cursor()}
      </div>
    </div>
  );
}
